/**
 * Windowed Splide pagination — tapers edge dots and clips to a rolling window
 * when there are more than `windowMax` pages.
 *
 * Opt-in per section:
 *   {% render 'splide-windowed-pagination' %}
 *   splide.mount()
 *   window.bindSplideWindowedPagination(splide)
 */
;(() => {
  if (typeof window.bindSplideWindowedPagination === 'function') return

  const WINDOW_MAX_DESKTOP = 10
  const WINDOW_MAX_MOBILE = 7
  const MOBILE_MQ = '(max-width: 767px)'
  // Match theme `.splide__pagination__page` (p-2): spacing from 0.5rem padding,
  // not flex gap. Inactive slot = 12 + 8 + 8; active slot = 28 (pill) + 8 + 8.
  const PAGE_PADDING_PX = 8
  const DOT_VISUAL_PX = 12
  const ACTIVE_PILL_PX = 28
  const DOT_SIZE_PX = DOT_VISUAL_PX + PAGE_PADDING_PX * 2 // 28
  const ACTIVE_SLOT_PX = ACTIVE_PILL_PX + PAGE_PADDING_PX * 2 // 44
  const DOT_HEIGHT_PX = DOT_SIZE_PX
  const DOT_GAP_PX = 0
  const ACTIVE_PILL_OVERHANG_PX = 0

  function getWindowMax(options) {
    if (typeof options.windowMax === 'number') return options.windowMax

    const isMobile = window.matchMedia(MOBILE_MQ).matches
    if (isMobile) return options.windowMaxMobile ?? WINDOW_MAX_MOBILE
    return options.windowMaxDesktop ?? WINDOW_MAX_DESKTOP
  }

  function getDotKind(posInWindow, maxDots, atStart, atEnd) {
    if (posInWindow < 0 || posInWindow >= maxDots) return 'hidden'

    const atLeftEdge = posInWindow === 0
    const nearLeftEdge = posInWindow === 1
    const atRightEdge = posInWindow === maxDots - 1
    const nearRightEdge = posInWindow === maxDots - 2

    if (atLeftEdge && !atStart) return 'edge'
    if (nearLeftEdge && !atStart) return 'near-edge'
    if (atRightEdge && !atEnd) return 'edge'
    if (nearRightEdge && !atEnd) return 'near-edge'

    return 'normal'
  }

  function getDotScale(kind) {
    switch (kind) {
      case 'edge':
        return 0.5
      case 'near-edge':
        return 0.75
      case 'hidden':
        return 0
      default:
        return 1
    }
  }

  function getActivePageIndex(splide, items) {
    const activeButton = splide.root.querySelector('.splide__pagination__page.is-active')
    if (activeButton) {
      const li = activeButton.closest('li')
      const fromDom = items.indexOf(li)
      if (fromDom >= 0) return fromDom
    }

    try {
      return splide.Components.Controller.toPage(splide.index)
    } catch {
      return splide.index
    }
  }

  // Tailwind / responsive utilities that must live on the shell (outside the
  // clipped window). Inner .splide__pagination always gets margin/padding zeroed.
  const SPACING_CLASS_RE = /(?:^|:)!?-?(?:m|p)(?:[trblxyse])?(?:-|$|\[)/
  const POSITION_CLASS_RE =
    /(?:^|:)!?-?(?:absolute|fixed|sticky|relative|static|inset|top|right|bottom|left)(?:-|$|\[)/
  const TRANSFORM_CLASS_RE = /(?:^|:)!?-?(?:translate|transform)/
  // Site-wide rule: pagination is always centered. Drop justify-* so legacy
  // !justify-start (and section flex-start) cannot left-align the shell.
  const JUSTIFY_CLASS_RE = /(?:^|:)!?justify-/

  function isNonZeroCssLength(value) {
    return Boolean(value) && value !== '0px' && value !== '0'
  }

  function centerPaginationShell(shell) {
    if (!shell) return
    shell.style.display = 'flex'
    shell.style.width = '100%'
    shell.style.justifyContent = 'center'
  }

  function transferLayoutToShell(paginationEl, shell) {
    const computed = window.getComputedStyle(paginationEl)
    const position = computed.position

    // Read stylesheet-driven box model before moving utilities off the ul
    const styleLayout = {
      marginTop: computed.marginTop,
      marginRight: computed.marginRight,
      marginBottom: computed.marginBottom,
      marginLeft: computed.marginLeft,
      paddingTop: computed.paddingTop,
      paddingRight: computed.paddingRight,
      paddingBottom: computed.paddingBottom,
      paddingLeft: computed.paddingLeft,
      transform: computed.transform,
      zIndex: computed.zIndex,
      top: computed.top,
      right: computed.right,
      bottom: computed.bottom,
      left: computed.left,
    }

    let movedSpacingUtility = false
    let movedPositionUtility = false
    let movedTransformUtility = false

    // Move layout utilities onto the shell so responsive pt-*/mt-*/top-*/etc. keep working
    for (const cls of [...paginationEl.classList]) {
      if (cls === 'splide__pagination') continue
      // Never transfer justify-* — shell is always centered
      if (JUSTIFY_CLASS_RE.test(cls)) {
        paginationEl.classList.remove(cls)
        continue
      }
      if (SPACING_CLASS_RE.test(cls)) movedSpacingUtility = true
      if (POSITION_CLASS_RE.test(cls)) movedPositionUtility = true
      if (TRANSFORM_CLASS_RE.test(cls)) movedTransformUtility = true
      shell.classList.add(cls)
      paginationEl.classList.remove(cls)
    }

    // Preserve vertical spacing / transforms from markup. Skip horizontal
    // margin/padding so leftover left offsets (e.g. margin-left: -5%) cannot
    // pull centered dots off-axis.
    ;['marginTop', 'marginBottom', 'paddingTop', 'paddingBottom', 'transform'].forEach((prop) => {
      if (paginationEl.style[prop]) {
        shell.style[prop] = paginationEl.style[prop]
        paginationEl.style[prop] = ''
      }
    })
    ;['marginLeft', 'marginRight', 'paddingLeft', 'paddingRight'].forEach((prop) => {
      paginationEl.style[prop] = ''
    })

    if (!movedPositionUtility) {
      if (position === 'absolute' || position === 'fixed' || position === 'sticky') {
        shell.style.position = position
        if (styleLayout.top !== 'auto') shell.style.top = styleLayout.top
        if (styleLayout.right !== 'auto') shell.style.right = styleLayout.right
        if (styleLayout.bottom !== 'auto') shell.style.bottom = styleLayout.bottom
        // Prefer inset-x / full-width centering over a lone left offset
        if (styleLayout.left !== 'auto' && styleLayout.right !== 'auto') {
          shell.style.left = styleLayout.left
          shell.style.right = styleLayout.right
        } else if (styleLayout.left === '0px' || styleLayout.left === '0') {
          shell.style.left = '0'
          shell.style.right = '0'
        } else if (styleLayout.right !== 'auto') {
          shell.style.right = styleLayout.right
        }
        shell.style.zIndex = styleLayout.zIndex === 'auto' ? '1' : styleLayout.zIndex
      } else if (position === 'relative') {
        shell.style.position = position
        if (styleLayout.zIndex !== 'auto') shell.style.zIndex = styleLayout.zIndex
      }
    } else if (styleLayout.zIndex !== 'auto') {
      shell.style.zIndex = styleLayout.zIndex
    }

    // Section <style> spacing targeting .splide__pagination (no Tailwind utilities)
    // Vertical only — horizontal insets fight site-wide centered pagination.
    if (!movedSpacingUtility) {
      if (isNonZeroCssLength(styleLayout.marginTop)) shell.style.marginTop = styleLayout.marginTop
      if (isNonZeroCssLength(styleLayout.marginBottom)) shell.style.marginBottom = styleLayout.marginBottom
      if (isNonZeroCssLength(styleLayout.paddingTop)) shell.style.paddingTop = styleLayout.paddingTop
      if (isNonZeroCssLength(styleLayout.paddingBottom)) shell.style.paddingBottom = styleLayout.paddingBottom
    }

    if (
      !movedTransformUtility &&
      styleLayout.transform &&
      styleLayout.transform !== 'none' &&
      !shell.style.transform
    ) {
      shell.style.transform = styleLayout.transform
    }

    // Theme buttons use p-2; windowed mode zeros ul padding, which can collapse
    // the in-flow gap. Only restore that inset when buttons actually had
    // non-zero padding — sections that zero page padding via CSS must not get
    // an extra top offset.
    const firstPage = paginationEl.querySelector('.splide__pagination__page')
    const pagePaddingTop = firstPage ? window.getComputedStyle(firstPage).paddingTop : '0px'
    const hadThemeButtonPadding = isNonZeroCssLength(pagePaddingTop)
    const isInFlow = position === 'static' || position === 'relative'
    if (
      isInFlow &&
      !movedSpacingUtility &&
      !isNonZeroCssLength(styleLayout.marginTop) &&
      !isNonZeroCssLength(styleLayout.paddingTop) &&
      hadThemeButtonPadding
    ) {
      shell.style.paddingTop = '0.5rem'
    }

    centerPaginationShell(shell)

    paginationEl.style.position = 'static'
    paginationEl.style.top = ''
    paginationEl.style.right = ''
    paginationEl.style.bottom = ''
    paginationEl.style.left = ''
    paginationEl.style.margin = '0'
    paginationEl.style.padding = '0'
    paginationEl.style.width = ''
    paginationEl.style.transform = ''
  }

  function ensureWindow(paginationEl) {
    const existing = paginationEl.closest('[data-pagination-window]')
    if (existing) return existing

    const shell = document.createElement('div')
    shell.className = 'splide-windowed-pagination-shell'

    const windowEl = document.createElement('div')
    windowEl.className = 'splide-windowed-pagination-window'
    windowEl.setAttribute('data-pagination-window', '')

    const parent = paginationEl.parentNode
    if (!parent) return null

    transferLayoutToShell(paginationEl, shell)

    parent.insertBefore(shell, paginationEl)
    shell.appendChild(windowEl)
    windowEl.appendChild(paginationEl)

    return windowEl
  }

  /** Unwrap shell + clear stale dots so a remount (or pagination:false) starts clean. */
  function teardownWindowedPagination(root) {
    if (!root) return

    root.removeAttribute('data-splide-windowed-pagination')
    if (root.style.overflow === 'visible') root.style.overflow = ''

    const shell = root.querySelector(':scope > .splide-windowed-pagination-shell')
    if (shell) {
      const paginationEl = shell.querySelector('.splide__pagination')
      if (paginationEl && shell.parentNode) {
        // Move layout utilities back onto the ul for the next mount
        for (const cls of [...shell.classList]) {
          if (cls === 'splide-windowed-pagination-shell') continue
          paginationEl.classList.add(cls)
        }
        paginationEl.replaceChildren()
        paginationEl.removeAttribute('style')
        shell.parentNode.insertBefore(paginationEl, shell)
      }
      shell.remove()
      return
    }

    const paginationEl = root.querySelector(':scope > .splide__pagination')
    if (paginationEl) {
      paginationEl.replaceChildren()
      paginationEl.removeAttribute('style')
    }
  }

  function updateWindowedPagination(splide, options) {
    const root = splide.root
    const paginationEl = root.querySelector(
      ':scope > .splide-windowed-pagination-shell .splide__pagination, :scope > .splide__pagination, .splide__pagination'
    )
    if (!paginationEl || !root.contains(paginationEl)) {
      teardownWindowedPagination(root)
      return
    }

    const items = [...paginationEl.querySelectorAll(':scope > li')]
    // No pages (e.g. single-card filter with pagination off) — drop leftover
    // wrapper/items from a previous multi-slide mount.
    if (!items.length) {
      teardownWindowedPagination(root)
      return
    }

    const windowEl = ensureWindow(paginationEl)
    if (!windowEl) return

    const windowMax = getWindowMax(options)
    const inactiveSlot = options.dotSize ?? DOT_SIZE_PX
    const activeSlot = options.activeSlot ?? ACTIVE_SLOT_PX
    const dotHeight = options.dotHeight ?? DOT_HEIGHT_PX
    const dotGap = options.dotGap ?? DOT_GAP_PX
    const total = items.length
    const active = getActivePageIndex(splide, items)

    // Trailing gap is included in every slotWidth; strip it from track/window totals.
    const slotWidth = (index) => (index === active ? activeSlot : inactiveSlot) + dotGap
    const sumSlots = (start, end) => {
      let sum = 0
      for (let i = start; i < end; i++) sum += slotWidth(i)
      return sum
    }

    root.setAttribute('data-splide-windowed-pagination', '')
    root.style.overflow = 'visible'

    const isWindowed = total > windowMax
    const maxDots = windowMax

    let targetOffset = 0
    let atStart = true
    let atEnd = true
    let translateX = 0
    // Any maxDots window that contains the active page has this pixel width.
    let windowWidth =
      activeSlot + (maxDots - 1) * inactiveSlot + Math.max(0, maxDots - 1) * dotGap

    if (isWindowed) {
      const contentWidth = Math.max(0, sumSlots(0, total) - dotGap)
      const activeCenter = sumSlots(0, active) + activeSlot / 2
      const maxTranslate = Math.max(0, contentWidth - windowWidth)

      // Center the expanding active pill in the clip (not index-based halfWindow).
      translateX = Math.max(0, Math.min(activeCenter - windowWidth / 2, maxTranslate))
      atStart = translateX <= 0.5
      atEnd = translateX >= maxTranslate - 0.5

      // Discrete page window for edge tapering: keep active inside, align to translateX.
      const minOff = Math.max(0, active - maxDots + 1)
      const maxOff = Math.min(active, total - maxDots)
      let bestErr = Infinity
      for (let offset = minOff; offset <= maxOff; offset++) {
        const err = Math.abs(sumSlots(0, offset) - translateX)
        if (err < bestErr) {
          bestErr = err
          targetOffset = offset
        }
      }
    } else {
      windowWidth = 0
    }

    const shell = windowEl.parentElement
    if (shell?.classList.contains('splide-windowed-pagination-shell')) {
      // Always re-assert center — section CSS / remounts can clear it.
      centerPaginationShell(shell)
    }

    // Size items first so active keeps 0.5rem L/R padding around the pill.
    items.forEach((item, i) => {
      const kind = isWindowed ? getDotKind(i - targetOffset, maxDots, atStart, atEnd) : 'normal'
      const button = item.querySelector('.splide__pagination__page')

      item.style.width = `${i === active ? activeSlot : inactiveSlot}px`
      item.style.height = `${dotHeight}px`
      item.style.margin = '0'
      item.style.display = 'flex'
      item.style.alignItems = 'center'
      item.style.justifyContent = 'center'
      item.style.flexShrink = '0'
      item.style.transformOrigin = 'center'
      item.style.transform = `scale(${getDotScale(kind)})`
      item.style.transition = 'transform 300ms ease-out, width 300ms ease'

      if (button) {
        if (kind === 'hidden') {
          button.tabIndex = -1
          button.setAttribute('aria-hidden', 'true')
        } else {
          button.tabIndex = 0
          button.removeAttribute('aria-hidden')
        }
      }
    })

    windowEl.style.height = `${dotHeight}px`
    // Shrink-wrap the clip/window so the shell can center it.
    const pillOverhang = options.activePillOverhang ?? ACTIVE_PILL_OVERHANG_PX
    windowEl.style.paddingLeft = `${pillOverhang}px`
    windowEl.style.paddingRight = `${pillOverhang}px`
    windowEl.style.boxSizing = 'content-box'
    windowEl.style.width = isWindowed ? `${windowWidth}px` : 'max-content'

    paginationEl.style.gap = `${dotGap}px`
    paginationEl.style.display = 'flex'
    paginationEl.style.alignItems = 'center'
    paginationEl.style.transform = isWindowed ? `translateX(${-translateX}px)` : 'none'
    paginationEl.style.justifyContent = 'flex-start'
    paginationEl.style.width = 'max-content'
    paginationEl.style.margin = '0'
    paginationEl.style.padding = '0'
    paginationEl.style.listStyle = 'none'
  }

  /**
   * @param {{ root: HTMLElement, index: number, on: Function, state?: number, Components?: object, options?: object }} splide
   * @param {{
   *   windowMax?: number,
   *   windowMaxMobile?: number,
   *   windowMaxDesktop?: number,
   *   dotSize?: number,
   *   dotGap?: number
   * }} [options]
   */
  function bindSplideWindowedPagination(splide, options = {}) {
    if (!splide?.root || typeof splide.on !== 'function') return

    if (splide.__windowedPaginationBound) {
      updateWindowedPagination(splide, options)
      return
    }

    splide.__windowedPaginationBound = true

    let rafId = 0
    let timeoutId = 0
    let lateTimeoutId = 0

    const refresh = () => {
      // Destroy clears timers, but also drop the flag first so a missed timer
      // cannot layout against a torn-down / remounting instance.
      if (!splide.__windowedPaginationBound) return
      updateWindowedPagination(splide, options)
    }

    const scheduleRefresh = () => {
      cancelAnimationFrame(rafId)
      clearTimeout(timeoutId)
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(refresh)
      })
      timeoutId = setTimeout(refresh, 50)
    }

    splide.on(
      'mounted pagination:mounted move moved active resized updated pagination:updated',
      scheduleRefresh
    )

    const mobileMq = window.matchMedia(MOBILE_MQ)
    const onBreakpoint = () => scheduleRefresh()
    if (typeof mobileMq.addEventListener === 'function') {
      mobileMq.addEventListener('change', onBreakpoint)
    } else if (typeof mobileMq.addListener === 'function') {
      mobileMq.addListener(onBreakpoint)
    }

    splide.on('destroy', () => {
      delete splide.__windowedPaginationBound
      if (typeof mobileMq.removeEventListener === 'function') {
        mobileMq.removeEventListener('change', onBreakpoint)
      } else if (typeof mobileMq.removeListener === 'function') {
        mobileMq.removeListener(onBreakpoint)
      }
      cancelAnimationFrame(rafId)
      clearTimeout(timeoutId)
      clearTimeout(lateTimeoutId)
      rafId = 0
      timeoutId = 0
      lateTimeoutId = 0
      teardownWindowedPagination(splide.root)
    })

    refresh()
    scheduleRefresh()
    requestAnimationFrame(() => {
      try {
        splide.refresh()
      } catch {
        /* ignore */
      }
      scheduleRefresh()
      // Separate id so this does not clobber scheduleRefresh's 50ms timer.
      clearTimeout(lateTimeoutId)
      lateTimeoutId = setTimeout(scheduleRefresh, 150)
    })
  }

  window.bindSplideWindowedPagination = bindSplideWindowedPagination
})()
